/**
 * Task #1416 — Sezione "Pipeline Health" condivisa.
 *
 * Variante `full` (default): usata in `pages/RAGAnalytics.tsx`, mostra
 * tutti i widget aggregati globalmente per tenant (no `agent_id`).
 *
 * Variante `compact`: usata nel modal `RAGAnalyticsDashboard` scoped per
 * `agent_id`. Espone solo i 4 widget per-agente (embedder coherence,
 * chunks load, bypass OCR, latency split) — i widget cluster-wide
 * (breakers, schema, HNSW, AI routing, misconfig globali, runtime
 * limits) sono nascosti perché sarebbero ridondanti rispetto alla page
 * principale.
 */
import { useMemo } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Cpu,
  Database,
  Gauge,
  Layers,
  RefreshCw,
  Shield,
  Sparkles,
  TrendingDown,
  XCircle,
  Zap,
} from 'lucide-react';
import { RAG_PLANE_LABELS } from '@easyflow/shared';
import { useRagPipelineHealth } from '@/hooks/useRagPipelineHealth';
import type {
  RagPipelineHealthResponse,
  RagHealthAnomaly,
  RerankBreakerSnapshot,
  HnswIndexHealthEntry,
  AgentChatCoherence,
  AgentChunkLoad,
} from '@easyflow/shared';

export type PipelineHealthVariant = 'full' | 'compact';

interface PipelineHealthSectionProps {
  variant?: PipelineHealthVariant;
  agentId?: string | null;
}

export function PipelineHealthSection({
  variant = 'full',
  agentId = null,
}: PipelineHealthSectionProps) {
  const { data, loading, error, lastFetchedAt, refetch } = useRagPipelineHealth({ agentId });

  if (loading && !data) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 p-6 flex items-center gap-3">
        <RefreshCw className="h-4 w-4 animate-spin text-blue-400" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Caricamento Pipeline Health…</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-2xl border border-rose-300 bg-rose-50 dark:bg-rose-500/10 dark:border-rose-500/30 p-4 text-sm text-rose-700 dark:text-rose-300 flex items-center gap-2">
        <XCircle className="h-4 w-4" />
        <span>Errore caricamento Pipeline Health: {error}</span>
        <button onClick={refetch} className="ml-auto text-xs underline">Riprova</button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <section className="space-y-4">
      <PipelineHeader
        anomalies={data.anomalies}
        generatedAt={data.generated_at}
        lastFetchedAt={lastFetchedAt}
        onRefresh={refetch}
        variant={variant}
        agentId={agentId}
      />

      {variant === 'full' && <ClusterWideWidgets data={data} />}
      <PerAgentWidgets data={data} variant={variant} />
    </section>
  );
}

// ─── Header + anomaly banner ────────────────────────────────────────────────

function PipelineHeader({
  anomalies,
  generatedAt,
  lastFetchedAt,
  onRefresh,
  variant,
  agentId,
}: {
  anomalies: RagHealthAnomaly[];
  generatedAt: string;
  lastFetchedAt: Date | null;
  onRefresh: () => void;
  variant: PipelineHealthVariant;
  agentId: string | null;
}) {
  const crit = anomalies.filter((a) => a.severity === 'crit');
  const warn = anomalies.filter((a) => a.severity === 'warn');
  const banner =
    crit.length > 0
      ? { tone: 'crit' as const, icon: XCircle, label: `${crit.length} anomalie critiche` }
      : warn.length > 0
        ? { tone: 'warn' as const, icon: AlertTriangle, label: `${warn.length} avvisi` }
        : { tone: 'ok' as const, icon: CheckCircle2, label: 'Tutto operativo' };

  const tone =
    banner.tone === 'crit'
      ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-300 dark:border-rose-500/30 text-rose-700 dark:text-rose-300'
      : banner.tone === 'warn'
        ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/30 text-amber-700 dark:text-amber-300'
        : 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
          <Activity className="h-4 w-4 text-white" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
          Pipeline Health
          {variant === 'compact' && agentId ? (
            <span className="ml-2 text-xs font-normal text-gray-400">(scope agente)</span>
          ) : null}
        </h2>
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-400">
          {lastFetchedAt && <span>Aggiornato {lastFetchedAt.toLocaleTimeString('it-IT')}</span>}
          <button
            onClick={onRefresh}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/5"
          >
            <RefreshCw className="h-3 w-3" />
            Aggiorna
          </button>
        </div>
      </div>

      <div className={`rounded-xl border p-3 flex items-start gap-2 ${tone}`}>
        <banner.icon className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{banner.label}</p>
          {anomalies.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-xs">
              {anomalies.slice(0, 6).map((a, idx) => (
                <li key={`${a.widget}-${idx}`} className="flex items-start gap-1.5">
                  <span className="font-mono opacity-60">[{a.widget}]</span>
                  <span>{a.message}</span>
                </li>
              ))}
              {anomalies.length > 6 && (
                <li className="opacity-70">… +{anomalies.length - 6} ulteriori</li>
              )}
            </ul>
          )}
        </div>
        <span className="text-[10px] opacity-60 shrink-0">
          {new Date(generatedAt).toLocaleTimeString('it-IT')}
        </span>
      </div>
    </div>
  );
}

// ─── Widget cluster-wide (solo variante full) ───────────────────────────────

function ClusterWideWidgets({ data }: { data: RagPipelineHealthResponse }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <SchemaGateCard data={data} />
      <AiRoutingCard data={data} />
      <RerankMonitorCard data={data} />
      <BreakersCard data={data} />
      <HnswCard data={data} />
      <RuntimeConfigCard data={data} />
      <MisconfigCard data={data} />
    </div>
  );
}

function HealthCard({
  icon: Icon,
  title,
  tone = 'neutral',
  degraded,
  reason,
  children,
}: {
  icon: typeof Activity;
  title: string;
  tone?: 'ok' | 'warn' | 'crit' | 'neutral';
  degraded?: boolean;
  reason?: string;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === 'crit'
      ? 'border-rose-300 dark:border-rose-500/40'
      : tone === 'warn'
        ? 'border-amber-300 dark:border-amber-500/40'
        : tone === 'ok'
          ? 'border-emerald-300 dark:border-emerald-500/30'
          : 'border-gray-200 dark:border-white/15';
  const iconTone =
    tone === 'crit'
      ? 'text-rose-500'
      : tone === 'warn'
        ? 'text-amber-500'
        : tone === 'ok'
          ? 'text-emerald-500'
          : 'text-blue-400';
  return (
    <div className={`rounded-2xl border bg-white dark:bg-white/5 ${toneClass} p-4 space-y-2`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${iconTone}`} />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {degraded && (
          <span title={reason} className="ml-auto text-[10px] uppercase tracking-wide text-rose-500 font-mono">
            degraded
          </span>
        )}
      </div>
      {degraded && reason && (
        <p className="text-[11px] text-rose-500 dark:text-rose-400 truncate" title={reason}>
          {reason}
        </p>
      )}
      <div className="text-sm text-gray-700 dark:text-gray-200">{children}</div>
    </div>
  );
}

function SchemaGateCard({ data }: { data: RagPipelineHealthResponse }) {
  const sg = data.schema_gate;
  const tone = sg.status === 'ok' ? 'ok' : 'crit';
  return (
    <HealthCard icon={Shield} title="Schema gate RAG" tone={tone} degraded={sg.degraded} reason={sg.reason}>
      <p className="text-sm">
        Stato: <strong className={sg.status === 'ok' ? 'text-emerald-500' : 'text-rose-500'}>{sg.status}</strong>
      </p>
      {sg.status === 'disabled' && sg.reason && (
        <p className="text-xs text-rose-500 dark:text-rose-400 mt-1">{sg.reason}</p>
      )}
      <p className="text-[11px] text-gray-400 mt-1">Verifica halfvec + indici al boot.</p>
    </HealthCard>
  );
}

function AiRoutingCard({ data }: { data: RagPipelineHealthResponse }) {
  const r = data.ai_routing;
  const unavailable = r.legs.filter((l) => l.model_status === 'unavailable').length;
  const deprecating = r.legs.filter((l) => l.model_status === 'deprecating').length;
  const tone = r.catalog_count === 0 || unavailable > 0 ? 'crit' : deprecating > 0 ? 'warn' : 'ok';
  return (
    <HealthCard icon={Cpu} title="Routing AI della pipeline RAG" tone={tone} degraded={r.degraded} reason={r.reason}>
      <p className="text-sm">
        Catalogo: <strong>{r.catalog_count}</strong> modelli attivi
        <span className="text-[11px] text-gray-400 ml-2">
          verificato {new Date(r.checked_at).toLocaleTimeString('it-IT')}
        </span>
      </p>
      {r.legs.length > 0 && (
        <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
          {r.legs.map((leg) => (
            <li key={leg.service_key} className="flex items-center gap-2 text-[11px]">
              <span className="text-gray-500 dark:text-gray-400 truncate flex-1" title={leg.service_key}>{leg.label_it}</span>
              <span className="font-mono text-gray-900 dark:text-white truncate max-w-[55%]" title={`${leg.provider} / ${leg.model}`}>
                {leg.provider === 'none' || !leg.model ? '—' : `${leg.provider} / ${leg.model}`}
              </span>
              <span className={`px-1 py-0.5 rounded text-[9px] uppercase shrink-0 ${
                leg.source === 'tenant' ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400' : 'bg-gray-500/10 text-gray-500'
              }`}>
                {leg.source}
              </span>
              {leg.model_status && leg.model_status !== 'active' && (
                <span className={`px-1 py-0.5 rounded text-[9px] uppercase shrink-0 ${
                  leg.model_status === 'unavailable' ? 'bg-rose-500/15 text-rose-500' : 'bg-amber-500/15 text-amber-500'
                }`}>
                  {leg.model_status}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </HealthCard>
  );
}

function RerankMonitorCard({ data }: { data: RagPipelineHealthResponse }) {
  const r = data.rerank_monitor;
  const tone = r.status === 'critical' ? 'crit' : r.status === 'warn' ? 'warn' : 'ok';
  return (
    <HealthCard icon={Gauge} title="Rerank monitor (1h)" tone={tone} degraded={r.degraded} reason={r.reason}>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-gray-400">degraded_single</p>
          <p className="font-semibold">{r.degraded_single_pct_1h}%</p>
        </div>
        <div>
          <p className="text-gray-400">degraded_none</p>
          <p className="font-semibold">{r.degraded_none_pct_1h}%</p>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">
        {r.sample_size} sample · ultimo alert: {r.last_alert_at ? new Date(r.last_alert_at).toLocaleTimeString('it-IT') : '—'}
      </p>
    </HealthCard>
  );
}

function BreakersCard({ data }: { data: RagPipelineHealthResponse }) {
  const items = data.breakers.breakers;
  const tone = items.some((b) => b.state === 'open')
    ? 'crit'
    : items.some((b) => b.state === 'half-open')
      ? 'warn'
      : 'ok';
  return (
    <HealthCard
      icon={Zap}
      title="Circuit breakers rerank"
      tone={tone}
      degraded={data.breakers.degraded}
      reason={data.breakers.reason}
    >
      <ul className="space-y-1.5">
        {items.map((b: RerankBreakerSnapshot) => (
          <li key={b.provider} className="flex items-center justify-between text-xs">
            <span className="font-mono">{b.provider}</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold ${
              b.state === 'open' ? 'bg-rose-500/15 text-rose-500'
                : b.state === 'half-open' ? 'bg-amber-500/15 text-amber-500'
                  : 'bg-emerald-500/15 text-emerald-500'
            }`}>
              {b.state}
            </span>
            <span className="text-gray-400">
              err {Math.round(b.error_rate * 100)}% · {b.samples} samp
            </span>
          </li>
        ))}
      </ul>
    </HealthCard>
  );
}

function HnswCard({ data }: { data: RagPipelineHealthResponse }) {
  const h = data.hnsw;
  const tone = !h.halfvec_available || h.missing_count > 0 ? 'crit' : 'ok';
  return (
    <HealthCard icon={Database} title="Indici HNSW halfvec" tone={tone} degraded={h.degraded} reason={h.reason}>
      <p className="text-sm">
        halfvec: <strong className={h.halfvec_available ? 'text-emerald-500' : 'text-rose-500'}>
          {h.halfvec_available ? 'disponibile' : 'mancante'}
        </strong>
      </p>
      <p className="text-sm">Indici mancanti: <strong>{h.missing_count}</strong> / {h.indexes.length}</p>
      {h.missing_count > 0 && (
        <ul className="mt-1 space-y-0.5 text-[11px] text-rose-500 dark:text-rose-400 max-h-24 overflow-y-auto">
          {h.indexes
            .filter((i: HnswIndexHealthEntry) => i.table_exists && i.column_exists && !i.index_exists)
            .map((i) => (
              <li key={i.index} className="font-mono">• {i.index}</li>
            ))}
        </ul>
      )}
    </HealthCard>
  );
}

function RuntimeConfigCard({ data }: { data: RagPipelineHealthResponse }) {
  const q = data.quality_config;
  const r = data.runtime_limits;
  return (
    <HealthCard icon={Layers} title="Runtime config RAG" degraded={q.degraded || r.degraded} reason={q.reason || r.reason}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <span className="text-gray-400">MMR λ</span>
        <span className="font-mono">{q.mmr_lambda.toFixed(2)}</span>
        <span className="text-gray-400">topK</span>
        <span className="font-mono">{q.topk.min}–{q.topk.max}</span>
        <span className="text-gray-400">expansion</span>
        <span className="font-mono">{q.expansion.enabled ? `+${q.expansion.expansions}` : 'off'}</span>
        <span className="text-gray-400">adaptive thr</span>
        <span className="font-mono">{q.adaptive_threshold.floor}–{q.adaptive_threshold.ceiling}</span>
        <span className="text-gray-400">decomp TTL</span>
        <span className="font-mono">{Math.round(r.decomp_cache.ttl_seconds / 60)}m</span>
        <span className="text-gray-400">multimodal RL</span>
        <span className="font-mono">{r.multimodal_rate_limit.max_requests}/{Math.round(r.multimodal_rate_limit.window_ms / 1000)}s</span>
      </div>
      <div className="mt-2 flex gap-1.5 flex-wrap">
        {Object.entries(q.flags).map(([k, v]) => (
          <span
            key={k}
            className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
              v ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-gray-200 dark:bg-white/5 text-gray-400'
            }`}
          >
            {k}
          </span>
        ))}
      </div>
    </HealthCard>
  );
}

function MisconfigCard({ data }: { data: RagPipelineHealthResponse }) {
  const m = data.misconfig;
  const tone = m.events_24h > 0 ? 'warn' : 'ok';
  return (
    <HealthCard icon={AlertTriangle} title="Misconfig embedder (24h)" tone={tone} degraded={m.degraded} reason={m.reason}>
      <p className="text-sm">
        Eventi: <strong>{m.events_24h}</strong>
      </p>
      <p className="text-[11px] text-gray-400 mt-1">
        Ultimo: {m.last_event_at ? new Date(m.last_event_at).toLocaleString('it-IT') : '—'}
      </p>
    </HealthCard>
  );
}

// ─── Widget per-agente (entrambe le varianti) ───────────────────────────────

function PerAgentWidgets({ data, variant }: { data: RagPipelineHealthResponse; variant: PipelineHealthVariant }) {
  const e = data.embedder;
  const lat = data.latency;
  const applicableCoherence = useMemo<AgentChatCoherence[]>(
    () => e.agents_chat_model_coherence.filter((a) => a.rag_applicable),
    [e.agents_chat_model_coherence],
  );
  const incoherent = useMemo<AgentChatCoherence[]>(
    () => applicableCoherence.filter((a) => !a.coherent),
    [applicableCoherence],
  );
  const heavy = useMemo<AgentChunkLoad[]>(
    () => e.chunks_per_agent.filter((c) => c.hnsw_warning),
    [e.chunks_per_agent],
  );

  const accentText = e.provider === 'gemini' ? 'text-violet-500' : 'text-sky-500';
  const accentBg = e.provider === 'gemini'
    ? 'bg-violet-500/10 border-violet-500/30'
    : 'bg-sky-500/10 border-sky-500/30';

  const cols = variant === 'compact' ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-2';

  return (
    <div className={`grid gap-4 ${cols}`}>
      {/* Embedder + bypass OCR */}
      <div className={`rounded-2xl border ${accentBg} p-4 space-y-2`}>
        <div className="flex items-center gap-2">
          <Sparkles className={`h-4 w-4 ${accentText}`} />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Embedder {e.provider ? (e.provider === 'gemini' ? 'Gemini 3072d' : 'OpenAI 3072d') : 'non configurato'}
          </h3>
          {e.locked_at && (
            <span className="ml-auto text-[10px] text-gray-400 font-mono">
              lock {new Date(e.locked_at).toLocaleDateString('it-IT')}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Modello: <span className="font-mono">{e.model || '—'}</span>
        </p>
        <div className="flex items-start gap-1.5 text-xs">
          {e.synthesis_coherence.coherent ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
          ) : (
            <XCircle className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <p className="text-gray-500 dark:text-gray-400">
              Sintesi RAG:{' '}
              <span className="font-mono text-gray-900 dark:text-white">
                {e.synthesis_coherence.provider ? `${e.synthesis_coherence.provider} / ${e.synthesis_coherence.model ?? '—'}` : '—'}
              </span>
              <span className="ml-1.5 px-1 py-0.5 rounded text-[9px] bg-gray-500/10 text-gray-500 dark:text-gray-400 font-mono">
                {e.synthesis_coherence.source}
              </span>
            </p>
            {!e.synthesis_coherence.coherent && e.synthesis_coherence.reason && (
              <p className="text-[10px] text-rose-500 dark:text-rose-400 mt-0.5">{e.synthesis_coherence.reason}</p>
            )}
          </div>
        </div>
        <div className="pt-1 flex items-center gap-2">
          <TrendingDown className={`h-4 w-4 ${accentText}`} />
          <span className="text-xs text-gray-500">Bypass OCR ({e.bypass_ocr.window_days}gg)</span>
        </div>
        {e.provider === 'gemini' ? (
          <p className={`text-xl font-bold ${accentText}`}>
            {e.bypass_ocr.count} doc
            <span className="text-xs text-gray-400 font-normal ml-2">
              risparmio ~ €{e.bypass_ocr.estimated_savings_eur.toFixed(2)}
            </span>
          </p>
        ) : (
          <p className="text-[11px] text-gray-400">
            N/A — OpenAI non legge nativamente le scansioni: OCR sempre richiesto.
          </p>
        )}
        {e.multimodal_rerank_mismatch.mismatch && (
          <div className="mt-2 rounded-lg border border-amber-300/60 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-2 space-y-1">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                Reranker text-only su chunk visivi
              </span>
            </div>
            <p className="text-[10px] text-amber-700/80 dark:text-amber-300/80 leading-snug">
              Reranker primario <span className="font-mono">{e.multimodal_rerank_mismatch.reranker || '—'}</span> non
              valuta il contenuto delle immagini. I chunk multimodali ricadono sul solo score retriever.
            </p>
            {e.multimodal_rerank_mismatch.last_seen && (
              <p className="text-[10px] text-amber-600/70 dark:text-amber-300/60 font-mono">
                ultima occorrenza {new Date(e.multimodal_rerank_mismatch.last_seen).toLocaleString('it-IT')}
                {e.multimodal_rerank_mismatch.observed_candidates !== null &&
                  ` · ${e.multimodal_rerank_mismatch.observed_candidates} chunk`}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Latency split */}
      <div className="rounded-2xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Latency split ({lat.window_days}gg · {lat.samples} sample)
          </h3>
          {lat.degraded && <span className="ml-auto text-[10px] text-rose-500 font-mono">degraded</span>}
        </div>
        {lat.samples === 0 ? (
          <p className="text-xs text-gray-400">Nessuna query RAG nel periodo.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <LatencyCell label="Retrieval" p50={lat.retrieval.p50} p95={lat.retrieval.p95} />
            <LatencyCell label="Inferential" p50={lat.inferential.p50} p95={lat.inferential.p95} />
            <LatencyCell label="Totale" p50={lat.total.p50} p95={lat.total.p95} highlight />
          </div>
        )}
        {lat.cache_hit_rate_pct !== null && (
          <p className="text-[11px] text-gray-400">
            Cache hit rate: <strong>{lat.cache_hit_rate_pct}%</strong>
          </p>
        )}
      </div>

      {/* Coerenza chat model */}
      <div className="rounded-2xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 p-4 space-y-2 md:col-span-2">
        <div className="flex items-center gap-2 flex-wrap">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Coerenza chat model agenti
          </h3>
          <span className="ml-auto flex items-center gap-2 text-[10px]">
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
              {applicableCoherence.length - incoherent.length} OK
            </span>
            {incoherent.length > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-500">
                {incoherent.length} incoerenti
              </span>
            )}
          </span>
          <p className="basis-full text-[10px] text-gray-400">
            Regola applicata solo al piano {RAG_PLANE_LABELS.rag} (chatbot/PA con KB indicizzata). Voce e agenti senza KB sono fuori ambito.
          </p>
        </div>
        {e.agents_chat_model_coherence.length === 0 ? (
          <p className="text-xs text-gray-400">
            {variant === 'compact'
              ? 'Questo agente non rientra nel piano RAG: nessuna KB indicizzata su cui valutare la coerenza embedder↔chat model.'
              : 'Nessun agente AI configurato.'}
          </p>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {e.agents_chat_model_coherence.map((a) => (
              <div key={a.agent_id} className="flex items-start gap-2 p-1.5 rounded hover:bg-gray-50 dark:hover:bg-white/5">
                {!a.rag_applicable ? (
                  <Shield className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
                ) : a.coherent ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{a.agent_name}</p>
                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-gray-500/10 text-gray-500 dark:text-gray-400 shrink-0">
                      {RAG_PLANE_LABELS[a.plane]}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400 font-mono">{a.llm_provider} / {a.chat_model}</p>
                  {!a.rag_applicable ? (
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      Fuori piano {RAG_PLANE_LABELS.rag}: nessuna KB indicizzata, coerenza embedder non applicabile.
                    </p>
                  ) : !a.coherent && a.reason ? (
                    <p className="text-[10px] text-rose-500 dark:text-rose-400 mt-0.5">{a.reason}</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* HNSW warning per agente (solo Gemini >10k) */}
      {heavy.length > 0 && (
        <div className="rounded-2xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 md:col-span-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                Carico HNSW elevato ({heavy.length} {heavy.length === 1 ? 'agente' : 'agenti'} oltre 10k chunk Gemini)
              </h3>
              <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
                Considerare partizionamento KB o tuning indice halfvec(3072) per mantenere recall sotto la soglia di degrado HNSW.
              </p>
              <ul className="mt-1.5 space-y-0.5">
                {heavy.slice(0, 5).map((a) => (
                  <li key={a.agent_id} className="text-[11px] text-amber-700 dark:text-amber-300">
                    • {a.agent_name}: <strong>{a.chunks.toLocaleString('it-IT')}</strong> chunk
                  </li>
                ))}
                {heavy.length > 5 && (
                  <li className="text-[11px] text-amber-700 dark:text-amber-300">… +{heavy.length - 5} altri</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LatencyCell({
  label,
  p50,
  p95,
  highlight,
}: {
  label: string;
  p50: number | null;
  p95: number | null;
  highlight?: boolean;
}) {
  return (
    <div className={`p-2 rounded ${highlight ? 'bg-gray-100 dark:bg-white/10' : ''}`}>
      <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{p50 != null ? `${p50}ms` : '—'}</p>
      <p className="text-[10px] text-gray-400">p95 {p95 != null ? `${p95}ms` : '—'}</p>
    </div>
  );
}

export default PipelineHealthSection;
